import { useEffect, useMemo, useState } from "react";

import { useARC } from "providers/ARC/ARCProvider";
import {
    fetchMobileStatus,
    getMobileNetworkApiBaseUrl,
    type MobileStatusResponse,
} from "lib/mobileNetworkApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";

const POLL_MS = 12000;

export type RobotMobileState =
    | { kind: "loading" }
    | { kind: "unavailable" }
    | { kind: "disabled" }
    | { kind: "enabled"; signal: number | null };

function powerIsOff(powerState: string | null | undefined): boolean {
    if (!powerState) return false;
    const p = powerState.toLowerCase();
    return p.includes("off") || p.includes("low");
}

function modemIsDisabled(status: MobileStatusResponse): boolean {
    if (!status.present) return true;
    if (powerIsOff(status.power_state)) return true;
    const st = (status.state ?? "").toLowerCase();
    return st === "disabled" || st === "disabling";
}

export function useRobotMobileStatus(): RobotMobileState {
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getMobileNetworkApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [state, setState] = useState<RobotMobileState>({ kind: "loading" });

    useEffect(() => {
        if (!baseUrl) {
            setState({ kind: "unavailable" });
            return;
        }

        let cancelled = false;

        const tick = async () => {
            try {
                const st = await fetchMobileStatus(baseUrl);
                if (cancelled) return;

                if (modemIsDisabled(st)) {
                    setState({ kind: "disabled" });
                    return;
                }

                const signal =
                    typeof st.signal_quality_percent === "number" &&
                    !Number.isNaN(st.signal_quality_percent)
                        ? st.signal_quality_percent
                        : null;
                setState({ kind: "enabled", signal });
            } catch {
                if (!cancelled) {
                    setState({ kind: "unavailable" });
                }
            }
        };

        setState({ kind: "loading" });
        void tick();
        const id = window.setInterval(() => void tick(), POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [baseUrl]);

    return state;
}
